/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-call */
import { CommonModule, NgOptimizedImage } from "@angular/common";
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  computed,
  signal,
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { Subject, of } from "rxjs";
import { catchError, takeUntil } from "rxjs/operators";

// PrimeNG imports
import { MessageService } from "primeng/api";
import { AccordionModule } from "primeng/accordion";
import { ButtonModule } from "primeng/button";
import { CheckboxModule } from "primeng/checkbox";
import { PopoverModule } from "primeng/popover";
import { SkeletonModule } from "primeng/skeleton";
import { TabViewModule } from "primeng/tabview";
import { TagModule } from "primeng/tag";
import { ToastModule } from "primeng/toast";
import { TooltipModule } from "primeng/tooltip";

// Services
import {
  QPilotAccessToken,
  QPilotUser,
} from "../../../../core/services/qpilot.service";
import { SessionService } from "../../../../core/services/session.service";
import { ProductsService } from "../../services/products.service";

// Models
import { QPilotProduct } from "../../../../core/models/qpilot/product.interface";

/**
 * Product Detail Page Component
 * View-only product details page matching old QMC behavior
 * Uses GET /api/Sites/{siteId}/Products/{externalId} endpoint
 */
@Component({
  selector: "app-product-detail",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NgOptimizedImage,
    AccordionModule,
    ButtonModule,
    CheckboxModule,
    PopoverModule,
    SkeletonModule,
    TabViewModule,
    TagModule,
    ToastModule,
    TooltipModule,
  ],
  templateUrl: "./product-detail.component.html",
  styleUrl: "./product-detail.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
  // Note: MessageService is NOT provided locally - we use the root instance
  // This ensures both component toast calls AND API interceptor error toasts
  // go to the same MessageService that the <p-toast> listens to
})
export class ProductDetailComponent implements OnInit, OnDestroy {
  // Input properties for embedded mode
  @Input() inputSiteId?: number;
  @Input() inputToken?: QPilotAccessToken;
  @Input() inputExternalId?: string;
  @Input() embeddedMode: boolean = false;

  // State signals
  readonly currentSiteId = signal<number | null>(null);
  readonly externalId = signal<string | null>(null);
  readonly product = signal<QPilotProduct | null>(null);
  readonly isLoading = signal(true);
  readonly activeTabIndex = signal(0);
  readonly imageLoadFailed = signal(false);

  // Computed
  readonly productTitle = computed(() => {
    return this.product()?.Title ?? "Product Details";
  });

  readonly productImageUrl = computed(() => {
    if (this.imageLoadFailed()) return null;
    return this.product()?.ImageUrl ?? this.product()?.ImageThumbUrl ?? null;
  });

  // Fallback placeholder image for ngSrc
  readonly fallbackImageUrl = "assets/images/product-placeholder.svg";

  readonly lastSyncedDate = computed(() => {
    const p = this.product();
    if (!p?.UpdatedUtc) return null;
    return new Date(p.UpdatedUtc);
  });

  // Availability computed
  readonly addToScheduledOrderEnabled = computed(() => {
    const p = this.product();
    if (!p?.Availability) return false;
    return p.Availability.includes("AddToScheduledOrder");
  });

  readonly processScheduledOrderEnabled = computed(() => {
    const p = this.product();
    if (!p?.Availability) return false;
    return p.Availability.includes("ProcessScheduledOrder");
  });

  // Cleanup
  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly cdr: ChangeDetectorRef,
    private readonly elementRef: ElementRef,
    private readonly sessionService: SessionService,
    private readonly productsService: ProductsService,
    private readonly messageService: MessageService,
  ) {}

  ngOnInit(): void {
    // Handle embedded mode inputs
    const element = this.elementRef.nativeElement as HTMLElement;

    // Get siteId from input/attribute
    const siteIdString =
      this.inputSiteId?.toString() ??
      element.getAttribute("input-site-id") ??
      element.getAttribute("inputSiteId");

    if (siteIdString) {
      this.currentSiteId.set(parseInt(siteIdString, 10));
    }

    // Get externalId from input/attribute
    const externalIdString =
      this.inputExternalId ??
      element.getAttribute("input-external-id") ??
      element.getAttribute("inputExternalId");

    if (externalIdString) {
      this.externalId.set(externalIdString);
    }

    // Get token from attribute
    const tokenString =
      element.getAttribute("input-token") ?? element.getAttribute("inputToken");

    if (
      tokenString &&
      (!this.inputToken ||
        typeof this.inputToken !== "object" ||
        !this.inputToken.TokenBearerAuth)
    ) {
      this.inputToken = {
        TokenBearerAuth: tokenString,
        TokenAuth: "",
        Id: 0,
        UserId: 0,
        CreatedUtc: new Date(),
        UpdatedUtc: new Date(),
        ExpiresUtc: new Date(),
        User: {} as QPilotUser,
        FirstLogin: 0,
      };
    }

    // Get embeddedMode from attribute
    const embeddedModeAttr = element.getAttribute("embedded-mode");
    if (embeddedModeAttr === "true") {
      this.embeddedMode = true;
    }

    // Initialize based on mode
    if (this.embeddedMode) {
      this.initializeEmbeddedMode();
    } else {
      this.initializeStandardMode();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ==================== Initialization ====================

  private initializeStandardMode(): void {
    // Get site ID from parent route and external ID from current route
    const siteId = Number(this.route.parent?.snapshot.paramMap.get("siteId"));
    const externalId = this.route.snapshot.paramMap.get("id"); // Route param is :id but contains externalId

    if (siteId && siteId > 0) {
      this.currentSiteId.set(siteId);
    }

    if (externalId) {
      this.externalId.set(externalId);
      this.loadProduct();
    } else {
      this.isLoading.set(false);
      this.messageService.add({
        severity: "error",
        summary: "Error",
        detail: "Invalid product ID",
      });
    }
  }

  private initializeEmbeddedMode(): void {
    const externalId = this.externalId();
    if (externalId) {
      this.loadProduct();
    } else {
      this.isLoading.set(false);
    }
  }

  private loadProduct(): void {
    const siteId = this.currentSiteId();
    const externalId = this.externalId();
    const accessToken = this.getAccessToken();

    if (!siteId || !externalId || !accessToken) {
      this.isLoading.set(false);
      return;
    }

    this.isLoading.set(true);
    this.imageLoadFailed.set(false);

    this.productsService
      .getProductByExternalId(siteId, externalId, accessToken)
      .pipe(
        takeUntil(this.destroy$),
        catchError((error) => {
          console.error("Failed to load product:", error);
          this.messageService.add({
            severity: "error",
            summary: "Error",
            detail: "Failed to load product details",
          });
          return of(null);
        }),
      )
      .subscribe((product) => {
        this.isLoading.set(false);

        if (product) {
          this.product.set(product);
        }

        this.cdr.markForCheck();
      });
  }

  // ==================== Public Methods ====================

  getAccessToken(): QPilotAccessToken | undefined {
    return this.inputToken ?? this.sessionService.accessToken ?? undefined;
  }

  onTabChange(index: number): void {
    this.activeTabIndex.set(index);
  }

  onImageError(event: Event): void {
    this.imageLoadFailed.set(true);
    this.cdr.markForCheck();
  }

  onBack(): void {
    this.navigateBack();
  }

  private navigateBack(): void {
    if (this.embeddedMode) {
      const event = new CustomEvent("qmc-navigate-back", {
        detail: {
          component: "product-detail",
          targetComponent: "products-list",
          siteId: this.currentSiteId(),
          externalId: this.externalId(),
        },
        bubbles: true,
        composed: true, // Required for shadow DOM crossing
      });
      this.elementRef.nativeElement.dispatchEvent(event);
    } else {
      void this.router.navigate(["../"], { relativeTo: this.route });
    }
  }

  // ==================== Helper Methods ====================

  getStatusSeverity(): "success" | "secondary" | "danger" {
    const product = this.product();
    if (!product) return "secondary";
    if (product.Valid === false) return "danger";
    return product.Active ? "success" : "secondary";
  }

  getStatusText(): string {
    const product = this.product();
    if (!product) return "Unknown";
    if (product.Valid === false) return "Invalid";
    return product.Active ? "Active" : "Inactive";
  }

  getStockSeverity(): "success" | "warn" | "danger" | "secondary" {
    const product = this.product();
    if (!product) return "secondary";
    const stock = product.Stock ?? 0;
    if (stock === 0) return "danger";
    if (stock < 10) return "warn";
    return "success";
  }

  getStockText(): string {
    const product = this.product();
    if (!product) return "N/A";
    const stock = product.Stock;
    if (stock === undefined || stock === null) return "N/A";
    if (stock === 0) return "Out of Stock";
    if (stock < 10) return `Low Stock (${stock})`;
    return `In Stock (${stock})`;
  }

  getStockLevelDisplay(): string {
    const product = this.product();
    if (!product) return "N/A";
    const stockLevel = product.StockLevel ?? "";
    const stock = product.Stock;

    // Format like old QMC: "In Stock (8)"
    let display = stockLevel;
    if (stockLevel === "InStock") display = "In Stock";
    else if (stockLevel === "LowStock") display = "Low Stock";
    else if (stockLevel === "OutOfStock") display = "Out of Stock";

    if (stock !== undefined && stock !== null) {
      display += ` (${stock})`;
    }

    return display;
  }

  formatPrice(price: number | null | undefined): string {
    if (price === null || price === undefined) return "N/A";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(price);
  }

  formatDate(date: Date | string | null | undefined): string {
    if (!date) return "Never";
    const d = typeof date === "string" ? new Date(date) : date;
    return d.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }

  formatShortDate(date: Date | string | null | undefined): string {
    if (!date) return "Never";
    const d = typeof date === "string" ? new Date(date) : date;
    return d.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
  }
}
